/**
 * Página de recepción de pedidos de compra
 * Se elige el pedido en un combo con los pedidos pendientes (los mismos que lista EstadosCompras) que
 * aún no se han recepcionado, se marca como recepcionado y se asigna el empleado que lo recibe
 */
import React, { useState, useContext, useEffect, useMemo } from "react";
import {
    Autocomplete,
    Box,
    Card,
    TextField,
    Select,
    MenuItem,
    Button,
    Typography,
    Divider,
    Alert,
    CircularProgress,
    useTheme,
    useMediaQuery
} from "@mui/material";
import MoveToInboxIcon from "@mui/icons-material/MoveToInbox";
import PersonIcon from "@mui/icons-material/Person";
import TagIcon from "@mui/icons-material/Tag";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { StateContext } from "../../../components/context/StateProvider";
import { FieldLabel } from "../components";
import ComprasService from "../api/compras.api";
import Utils from "../../../utils/Utils";
import Toasts from "../../../components/notificacion/Toasts";
import successSound from "../../../assets/audio/success.mp3";
import errorSound from "../../../assets/audio/error.mp3";
import AuthService from "../../../services/auth.service";
import UsuariosService from "../../../services/usuarios.service";
import usePedidosCompra from "../hooks/usePedidosCompra";
import { ALTURA_BOTON, BOTON_PRIMARIO_SX } from "../../../components/ui/buttonStyles";

/** Texto de cada pedido en el combo: número y proveedor */
const etiquetaPedido = (pedido) =>
    pedido.proveedor ? `${pedido.numeroPedido} · ${pedido.proveedor}` : pedido.numeroPedido;

const RecepcionCompras = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("md"), { noSsr: true });
    // getCurrentUser() devuelve un objeto nuevo cada vez: se fija al montar
    const user = useMemo(() => AuthService.getCurrentUser(), []);
    const { state, updateState } = useContext(StateContext);

    const { pedidos, loading: loadingPedidos, error: errorPedidos, recargar } = usePedidosCompra(state.year);
    const [pedido, setPedido] = useState(null);
    // Los ya recepcionados no se ofrecen: el backend respondería 409 si se repitiera
    const pedidosSinRecepcionar = useMemo(() => pedidos.filter((p) => !p.recepcionado), [pedidos]);
    const [empleados, setEmpleados] = useState([]);
    const [recepcionista, setRecepcionista] = useState("");
    const [loading, setLoading] = useState(false);
    const [ultimaRecepcion, setUltimaRecepcion] = useState(null);

    // DECISION: el selector se muestra siempre, también a no admins. Quien recepciona
    // no tiene por qué ser quien está logueado en el terminal del muelle; solo se
    // preselecciona al usuario actual como valor más probable.
    useEffect(() => {
        const fetchEmpleados = async () => {
            const usuarios = await UsuariosService.listarUsuariosMap();
            const opciones = usuarios.map((u) => ({ label: u.nombre, value: u.nombre.toLowerCase() }));
            setEmpleados(opciones);

            const usuarioLogueado = user?.username?.toLowerCase() || "";
            if (opciones.some((o) => o.value === usuarioLogueado)) {
                setRecepcionista(usuarioLogueado);
            }
        };
        fetchEmpleados();
    }, [user]);

    const notificar = (mensaje, tipo, sonido) => {
        updateState({ toasts: [...state.toasts, Toasts.generateToast(mensaje, tipo)] });
        Utils.playAudio(sonido);
    };

    const handleSubmit = async (event) => {
        event.preventDefault();

        if (!recepcionista) {
            notificar("Elige el empleado que recepciona", "error", errorSound);
            return;
        }
        if (!pedido) {
            notificar("Elige el pedido de compra", "error", errorSound);
            return;
        }
        const numero = pedido.numeroPedido;

        setLoading(true);
        try {
            const registro = await ComprasService.recepcionar(numero, state.year, recepcionista);
            setUltimaRecepcion(registro);
            notificar(`Pedido <strong>${numero}</strong> recepcionado`, "success", successSound);
            setPedido(null);
            // El pedido pasa a RECEPCIONADO: se recarga para que el combo lo refleje
            recargar();
        } catch (error) {
            notificar(error.message, "error", errorSound);
        }
        setLoading(false);
    };

    return (
        <Box sx={{ width: "100%" }}>
            <Card sx={{ boxShadow: "0 2px 8px rgba(0,0,0,0.1)", borderRadius: 2 }}>
                {/* Página de solo formulario: header y formulario comparten el Box con p: 3 */}
                <Box sx={{ p: 3 }}>
                    <Typography
                        variant={isMobile ? "h6" : "h5"}
                        sx={{
                            fontWeight: 700,
                            color: "#212529",
                            mb: 0.5,
                            display: "flex",
                            alignItems: "center",
                            gap: 1
                        }}
                    >
                        <MoveToInboxIcon sx={{ fontSize: isMobile ? 24 : 28 }} />
                        Recepcionar
                    </Typography>
                    <Typography variant="body2" sx={{ color: "#6c757d" }}>
                        Marca un pedido de compra como recepcionado y asigna quién lo recibe
                    </Typography>

                    <Divider sx={{ my: 2 }} />

                    <Box
                        component="form"
                        onSubmit={handleSubmit}
                        sx={{ display: "flex", flexDirection: "column", gap: { xs: 2, sm: 2.5 } }}
                    >
                        {/* Recepcionista */}
                        <Box>
                            <FieldLabel icon={PersonIcon}>Recepcionado por *</FieldLabel>
                            <Select
                                id="recepcionista"
                                value={recepcionista}
                                onChange={(e) => setRecepcionista(e.target.value)}
                                displayEmpty
                                fullWidth
                                sx={{ backgroundColor: "#fff", "& .MuiSelect-select": { py: { xs: 1.5, sm: 1.75 } } }}
                            >
                                <MenuItem value="" disabled>
                                    Elige empleado
                                </MenuItem>
                                {empleados.map((option) => (
                                    <MenuItem key={option.value} value={option.value}>
                                        {option.label}
                                    </MenuItem>
                                ))}
                            </Select>
                        </Box>

                        {/* Pedido de compra */}
                        <Box>
                            <FieldLabel icon={TagIcon}>Pedido de compra *</FieldLabel>
                            {errorPedidos && (
                                <Alert severity="error" sx={{ mb: 1 }}>
                                    {errorPedidos}
                                </Alert>
                            )}
                            <Autocomplete
                                id="pedido-compra"
                                options={pedidosSinRecepcionar}
                                value={pedido}
                                onChange={(_, valor) => setPedido(valor)}
                                getOptionLabel={etiquetaPedido}
                                isOptionEqualToValue={(opcion, valor) => opcion.numeroPedido === valor.numeroPedido}
                                renderOption={(props, opcion) => (
                                    <li {...props} key={opcion.numeroPedido}>
                                        <Box>
                                            <Typography sx={{ fontWeight: 600, fontSize: 14 }}>
                                                {opcion.numeroPedido}
                                            </Typography>
                                            <Typography sx={{ fontSize: 13, color: "#6c757d" }}>
                                                {opcion.proveedor || "Sin proveedor"}
                                            </Typography>
                                        </Box>
                                    </li>
                                )}
                                loading={loadingPedidos}
                                loadingText={
                                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                        <CircularProgress size={16} sx={{ color: "#cc071e" }} />
                                        Cargando pedidos...
                                    </Box>
                                }
                                noOptionsText="No hay pedidos de compra pendientes de recepcionar"
                                fullWidth
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        placeholder="Busca por número o proveedor"
                                        sx={{
                                            "& .MuiOutlinedInput-root": { backgroundColor: "#fff" },
                                            "& .MuiInputBase-input": { py: { xs: 1, sm: 1.25 } }
                                        }}
                                    />
                                )}
                            />
                        </Box>

                        <Button
                            type="submit"
                            variant="contained"
                            fullWidth
                            disabled={loading}
                            startIcon={
                                loading ? (
                                    <CircularProgress size={20} sx={{ color: "#fff" }} />
                                ) : (
                                    <CheckCircleIcon sx={{ fontSize: { xs: 18, sm: 20 } }} />
                                )
                            }
                            sx={{ ...BOTON_PRIMARIO_SX, height: ALTURA_BOTON.FILTRO, mt: { xs: 1, sm: 0.5 } }}
                        >
                            {loading ? "Procesando..." : "Recepcionar"}
                        </Button>
                    </Box>

                    {/* Última recepción: confirma al operario qué ha quedado registrado */}
                    {ultimaRecepcion && (
                        <>
                            <Divider sx={{ my: 2 }} />
                            <Alert severity="success" onClose={() => setUltimaRecepcion(null)}>
                                <strong>Pedido {ultimaRecepcion.pedido}</strong>
                                {ultimaRecepcion.proveedor && ` · ${ultimaRecepcion.proveedor}`}
                                <br />
                                Recepcionado por <strong>{ultimaRecepcion.recepcionado}</strong>
                                {ultimaRecepcion.fechaRecepcion &&
                                    ` el ${new Date(ultimaRecepcion.fechaRecepcion).toLocaleString("es-ES")}`}
                            </Alert>
                        </>
                    )}
                </Box>
            </Card>
        </Box>
    );
};

export default RecepcionCompras;
